import { volcaColor } from "./spreadsheet-theme";

/**
 * Marks where the pinned columns end with a rule down the grid — the convention
 * every other spreadsheet uses, and what a frozen boundary actually is: a seam
 * the content scrolls past, not a property of the columns beside it.
 *
 * This replaces a green tint on the frozen headers, which had two problems. It
 * claimed a colour role it had no business claiming (green is Volca's
 * interactive colour, and a pinned column is not an action), and it could not
 * survive being used: Glide picks a header's fill as
 * `selected ? accentColor : hasSelectedCell ? bgHeaderHasFocus : bgHeader`, so
 * the tint disappeared the moment you touched a cell in that column — exactly
 * when you are most likely to be looking for it. A drawn rule sits outside that
 * precedence and is always there.
 */
const DIVIDER_WIDTH_PX = 2;

const frozenDividerColor = () => volcaColor("--line");

/**
 * Draws the seam on the right edge of the last pinned column.
 *
 * Called from both the cell and header painters so the rule runs unbroken from
 * the column letters to the bottom of the sheet. Glide paints cells and headers
 * in separate passes, and there is no single surface spanning both.
 *
 * @param col zero-based data column index, as Glide's draw callbacks report it
 * @param frozenColumn how many leading columns are pinned; null or 0 for none
 */
export const drawFrozenColumnDivider = (
  ctx: CanvasRenderingContext2D,
  rect: { x: number; y: number; width: number; height: number },
  col: number,
  frozenColumn: number | null,
): void => {
  if (!frozenColumn || col !== frozenColumn - 1) return;

  ctx.save();
  ctx.fillStyle = frozenDividerColor();
  // Sat on the inside of the edge rather than centred on it: half a rule drawn
  // past the column's own bounds is clipped away by the next paint.
  ctx.fillRect(
    rect.x + rect.width - DIVIDER_WIDTH_PX,
    rect.y,
    DIVIDER_WIDTH_PX,
    rect.height,
  );
  ctx.restore();
};
